import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, Tag } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/site";
import WhatsAppIcon from "../components/WhatsAppIcon";
import ContentTab from "../components/admin/ContentTab";
import BlogTab from "../components/admin/BlogTab";
import IntegrationsTab from "../components/admin/IntegrationsTab";
import UsersTab from "../components/admin/UsersTab";
import SeoTab from "../components/admin/SeoTab";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const statusCls = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-reef-light text-reef border-reef/30",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
};

const utmLabel = (u) =>
  [u?.source, u?.medium, u?.campaign].filter(Boolean).join(" / ") ||
  (u?.gclid ? "google / ads (auto-tag)" : "campaign visit");

function UtmBadge({ utm, testid }) {
  if (!utm) return null;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-mono text-[10px] tracking-[0.08em] uppercase"
    >
      <Tag className="w-3 h-3" />
      {utmLabel(utm)}
    </span>
  );
}

function SourceSummary({ items, testid }) {
  if (!items.length) return null;
  const counts = {};
  let direct = 0;
  items.forEach((it) => {
    if (it.utm) {
      const k = utmLabel(it.utm);
      counts[k] = (counts[k] || 0) + 1;
    } else {
      direct += 1;
    }
  });
  return (
    <div data-testid={testid} className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-400">Sources:</span>
      <span className="px-3 py-1 rounded-full bg-sand-soft text-slate-600 font-mono text-[10px] tracking-[0.08em] uppercase">
        Direct / no campaign ({direct})
      </span>
      {Object.entries(counts).map(([k, c]) => (
        <span
          key={k}
          className="px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-mono text-[10px] tracking-[0.08em] uppercase"
        >
          {k} ({c})
        </span>
      ))}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);

  const load = useCallback(async () => {
    try {
      const [b, l] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
      ]);
      setBookings(b.data.bookings || []);
      setLeads(l.data.leads || []);
    } catch {
      toast.error("Could not load dashboard data.");
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        if (res.data.role === "editor") setTab("content");
        load();
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser(res.data);
      setTab(res.data.role === "editor" ? "content" : "bookings");
      load();
      toast.success("Welcome back!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, { withCredentials: true });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-sand-bg flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-reef animate-pulse">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-sand-bg flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm bg-white border border-sand-border rounded-3xl p-8"
        >
          <img src="/logo.png" alt="Reef Deco" className="h-11 w-auto" />
          <p className="mt-2 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-400">Team login</p>
          <form onSubmit={login} className="mt-7 space-y-4">
            <input
              type="email"
              data-testid="admin-login-email-input"
              placeholder="Email"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className="w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
              required
            />
            <input
              type="password"
              data-testid="admin-login-password-input"
              placeholder="Password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition"
              required
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit-btn"
              className="w-full rounded-full bg-reef text-sand-bg py-3.5 font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors"
            >
              Sign in
            </button>
          </form>
          <Link to="/" className="block mt-5 text-center font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 hover:text-reef transition-colors">
            ← Back to site
          </Link>
        </motion.div>
      </div>
    );
  }

  const isSuper = !!user.is_super;
  const isAdminRole = user.role === "admin";
  const isEditor = user.role === "editor";
  const canContent = isAdminRole || isEditor;
  const TABS = [
    ...(isEditor ? [] : [["bookings", `Bookings (${bookings.length})`], ["leads", `Leads (${leads.length})`]]),
    ...(canContent ? [["content", "Site content"], ["blog", "Blog"], ["seo", "SEO & AEO"]] : []),
    ...(isAdminRole ? [["integrations", "Integrations"]] : []),
    ...(isSuper ? [["users", "Team users"]] : []),
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand-bg">
      <header className="bg-white border-b border-sand-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Reef Deco" className="h-8 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 hidden sm:inline">Concierge</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="p-2.5 rounded-full border border-sand-border text-reef hover:bg-reef-light transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-sand-border text-slate-600 font-mono text-[11px] tracking-[0.12em] uppercase hover:text-red-600 hover:border-red-200 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-7">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-2.5 rounded-full font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
                tab === id ? "bg-reef text-sand-bg" : "border border-sand-border text-slate-600 hover:border-reef/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="space-y-4">
            <SourceSummary items={bookings} testid="admin-bookings-sources-summary" />
            {bookings.length === 0 && (
              <p data-testid="admin-bookings-empty" className="text-sm text-slate-500 italic py-10 text-center">
                No bookings yet — new free visit requests will appear here.
              </p>
            )}
            {bookings.map((b, i) => (
              <div
                key={b.id}
                data-testid={`admin-booking-row-${i}`}
                className="bg-white border border-sand-border rounded-2xl p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-serif text-lg text-slate-900">{b.name}</p>
                    <span className={`px-3 py-1 rounded-full border font-mono text-[10px] tracking-[0.1em] uppercase ${statusCls[b.status] || statusCls.pending}`}>
                      {b.status}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-xs text-reef">{b.date} · {b.slot_label}</p>
                  <p className="mt-1.5 text-sm text-slate-600">
                    {b.address}{b.apartment ? `, ${b.apartment}` : ""}
                    {b.windows_count ? ` · ${b.windows_count} windows` : ""}
                  </p>
                  {b.message && <p className="mt-1 text-sm text-slate-500 italic">“{b.message}”</p>}
                  <p className="mt-1.5 font-mono text-[11px] text-slate-400">
                    {b.phone}{b.whatsapp ? ` · WA ${b.whatsapp}` : ""}{b.email ? ` · ${b.email}` : ""}
                  </p>
                  {b.utm && <div className="mt-2"><UtmBadge utm={b.utm} testid={`admin-booking-utm-${i}`} /></div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <a
                    data-testid={`admin-booking-whatsapp-${i}`}
                    href={`https://wa.me/${(b.whatsapp || b.phone || "").replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-full bg-[#25D366] text-white hover:scale-105 transition-transform"
                    aria-label="WhatsApp customer"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                  </a>
                  <select
                    data-testid={`admin-booking-status-${i}`}
                    value={b.status}
                    onChange={(e) => setStatus(b.id, e.target.value)}
                    className="rounded-xl border border-sand-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reef/40"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-4">
            <SourceSummary items={leads} testid="admin-leads-sources-summary" />
            {leads.length === 0 && (
              <p data-testid="admin-leads-empty" className="text-sm text-slate-500 italic py-10 text-center">
                No estimate leads yet.
              </p>
            )}
            {leads.map((l, i) => (
              <div key={l.id} data-testid={`admin-lead-row-${i}`} className="bg-white border border-sand-border rounded-2xl p-5 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg text-slate-900">{l.name}</p>
                    <p className="font-mono text-[11px] text-slate-400 mt-0.5">
                      WA {l.whatsapp}{l.email ? ` · ${l.email}` : ""}
                    </p>
                    {l.utm && <div className="mt-2"><UtmBadge utm={l.utm} testid={`admin-lead-utm-${i}`} /></div>}
                  </div>
                  <p className="font-mono text-lg text-reef font-semibold">{l.total?.toLocaleString()} AED</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(l.windows || []).map((w, wi) => (
                    <span key={wi} className="px-3 py-1.5 rounded-full bg-sand-soft text-slate-600 text-xs">
                      {w.label}: {w.width}×{w.height}cm · {w.fabric}{w.motorized ? " · motor" : ""} · {w.price} AED
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "content" && canContent && <ContentTab />}
        {tab === "blog" && canContent && <BlogTab />}
        {tab === "seo" && canContent && <SeoTab />}
        {tab === "integrations" && isAdminRole && <IntegrationsTab />}
        {tab === "users" && isSuper && <UsersTab currentUser={user} />}
      </div>
    </div>
  );
}
